import React, {
  useEffect,
  Suspense,
  lazy,
  useState,
  useLayoutEffect,
  useRef,
} from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
} from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";

// Regular imports - needed immediately
import { GlobalAudioPlayer } from "./components/GlobalAudioPlayer";
import LoadingSpinner from "./components/common/LoadingSpinner";
// import ThemeToggle from "./components/ThemeToggle";
import CartIcon from "./components/cart/CartIcon";
import { AudioPlayerProvider } from "./contexts/AudioPlayerContext";
import { useAudioPlayer } from "./contexts/AudioPlayerContext";
import { LiveShowProvider } from "./contexts/LiveShowContext";
import { GlobalLiveShowChangeProvider } from "./contexts/GlobalLiveShowChangeContext";
import { Navigation } from "./components/Navigation";
import { CMSProvider } from "./contexts/CMSContext";
import { ShoppingCartProvider } from "./contexts/ShoppingCartContext";
import {
  ShoppingCartModalProvider,
  useShoppingCartModal,
} from "./contexts/ShoppingCartModalContext";
import { AddToCartFeedbackProvider } from "./contexts/AddToCartFeedbackContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import {
  AnalyticsConsentProvider,
  useAnalyticsConsent,
} from "./contexts/AnalyticsConsentContext";
import { useShoppingCart } from "./hooks/useShoppingCart";
import { queryClient } from "./lib/queryClient";
import { performanceServiceManager } from "./services/performanceServiceManager";
import {
  cachingStrategyService,
  DataType,
} from "./services/cachingStrategyService";
import checkoutAnalyticsService from "./services/checkoutAnalyticsService";
import { GlobalErrorProvider } from "./contexts/GlobalErrorContext";
import globalErrorReportingService from "./services/globalErrorReportingService";
import { createBackgroundGradient } from "./constants/theme";
import eruptionLogo from "./assets/logos/logo.png";
import eruptionLogoMobile from "./assets/logos/logo-mobile.png";
import { debugLog, debugWarn } from "./utils/debugLog";
import SiteFooter from "./components/layout/SiteFooter";
import AnalyticsConsentBanner from "./components/layout/AnalyticsConsentBanner";
import Seo from "./components/Seo";
import HomePage from "./components/HomePage";
import OptimizedContentImage from "./components/common/OptimizedContentImage";
import ChunkLoadErrorBoundary from "./components/common/ChunkLoadErrorBoundary";
import {
  attemptChunkLoadRecovery,
  isLikelyChunkLoadError,
} from "./components/common/chunkLoadRecovery";

// Utility imports
import "./utils/debugCart"; // Import debug utilities
import "./utils/testEmbeddedCheckout"; // Import embedded checkout test utilities
import "./utils/testModeHelper"; // Import to make global debugging functions available
import "./index.css";

const isDevEnvironment = process.env.NODE_ENV !== "production";

const RAW_IMAGE_CDN_BASE = process.env.REACT_APP_IMAGE_CDN_BASE?.trim();
const DEFAULT_IMAGE_CDN_ORIGIN =
  "https://station-public-images-prd.s3.eu-west-2.amazonaws.com";
const IMAGE_CDN_ORIGIN = (() => {
  if (RAW_IMAGE_CDN_BASE) {
    try {
      const url = new URL(RAW_IMAGE_CDN_BASE);
      return `${url.protocol}//${url.host}`;
    } catch (error) {
      debugWarn?.(
        "⚠️ Failed to parse REACT_APP_IMAGE_CDN_BASE for preconnect:",
        error
      );
    }
  }
  return DEFAULT_IMAGE_CDN_ORIGIN;
})();

if (isDevEnvironment) {
  // Load the dashboard test harness only during development to avoid bundling it in production
  void import("./tests/dashboard/DashboardFunctionalityTest");
}

const ReactQueryDevtoolsLazy = isDevEnvironment
  ? lazy(() =>
      import("@tanstack/react-query-devtools").then((module) => ({
        default: module.ReactQueryDevtools,
      }))
    )
  : null;

// Lazy load page components for better bundle splitting
const SchedulePage = lazy(() => import("./components/SchedulePage"));
const ShowsPage = lazy(() => import("./components/ShowsPage"));
const MixesPage = lazy(() => import("./components/MixesPage"));
const MixDetailPage = lazy(() => import("./components/MixDetailPage"));
const ShowDetailsPage = lazy(() => import("./components/ShowDetailsPage"));
const PresentersPage = lazy(() => import("./components/PresentersPage"));
const PresenterDetailPage = lazy(
  () => import("./components/PresenterDetailPage")
);
const AboutPage = lazy(() => import("./components/AboutPage"));
const BlogPage = lazy(() => import("./components/BlogPage"));
const BlogPostPage = lazy(() => import("./components/BlogPostPage"));
const EventsPage = lazy(() => import("./components/EventsPage"));
const EventPage = lazy(() => import("./components/EventPage"));
const MobileNavigationDrawer = lazy(
  () => import("./components/MobileNavigationDrawer")
);
const ShoppingCartModal = lazy(
  () => import("./components/cart/ShoppingCartModal")
);
const GalleriesPage = lazy(() => import("./components/PhotosPage"));
const GalleryPage = lazy(() => import("./components/PhotoPage"));
const UnifiedStorePage = lazy(() => import("./components/UnifiedStorePage"));
const OrderConfirmationPage = lazy(
  () => import("./components/OrderConfirmationPage")
);
const ContactPage = lazy(() => import("./components/ContactPage"));
const AdvertisePage = lazy(() => import("./components/AdvertisePage"));
const PrivacyPolicyPage = lazy(() => import("./components/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("./components/TermsPage"));
const LegalNoticePage = lazy(() => import("./components/LegalNoticePage"));
const ServerErrorPage = lazy(() => import("./components/ServerErrorPage"));
const MaintenancePage = lazy(() => import("./components/MaintenancePage"));
const NotFoundPage = lazy(() => import("./components/NotFoundPage"));
const NewsletterPage = lazy(() => import("./components/NewsletterPage"));
const SentryTestPage = lazy(() => import("./components/dev/SentryTestPage"));

const isSentryTestPageEnabled =
  (process.env.REACT_APP_SENTRY_TEST_PAGE_ENABLED ?? "")
    .toString()
    .trim()
    .toLowerCase() === "true";

const isNewsletterEnabled =
  (process.env.REACT_APP_NEWSLETTER_ENABLED ?? "")
    .toString()
    .trim()
    .toLowerCase() === "true";

// Lazy load development-only components
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PerformanceDashboard = isDevEnvironment
  ? lazy(() => import("./archive/components/dev/PerformanceDashboard"))
  : null;

const scheduleBrowserIdleTask = (callback: () => void): (() => void) => {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const idleWindow = window as Window &
    typeof globalThis & {
      requestIdleCallback?: (
        callback: IdleRequestCallback,
        options?: IdleRequestOptions
      ) => number;
      cancelIdleCallback?: (handle: number) => void;
    };

  if (typeof idleWindow.requestIdleCallback === "function") {
    const idleCallbackId = idleWindow.requestIdleCallback(() => {
      callback();
    }, { timeout: 2000 });

    return () => {
      idleWindow.cancelIdleCallback?.(idleCallbackId);
    };
  }

  const timeoutId = globalThis.setTimeout(callback, 1200);
  return () => {
    globalThis.clearTimeout(timeoutId);
  };
};

const AppContent: React.FC = () => {
  const { items } = useShoppingCart();
  const { isCartOpen, openCart, closeCart } = useShoppingCartModal();
  const { mode } = useTheme();
  const { playLiveStream } = useAudioPlayer();
  const { shouldEnableAnalytics } = useAnalyticsConsent();
  const location = useLocation();
  const initialPathnameRef = useRef(location.pathname);
  const cartItemCount = items.reduce((total, item) => total + item.quantity, 0);
  const cartAnnouncement =
    cartItemCount > 0
      ? `${cartItemCount} item${cartItemCount === 1 ? "" : "s"} in cart`
      : "Cart is empty";
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // Preload critical data on app initialization
  useEffect(() => {
    const initialPathname = initialPathnameRef.current;
    const preloadOptions =
      initialPathname === "/"
        ? undefined
        : { exclude: [DataType.HOMEPAGE_DATA] };

    let cancelled = false;
    const cancelScheduledPreload = scheduleBrowserIdleTask(() => {
      void (async () => {
        try {
          debugLog("🚀 Starting deferred critical data preloading...");
          await cachingStrategyService.preloadCriticalData(preloadOptions);
          if (!cancelled) {
            debugLog("✅ Critical data preloading completed");
          }
        } catch (error) {
          if (!cancelled) {
            debugWarn("⚠️ Critical data preloading failed:", error);
          }
        }
      })();
    });

    return () => {
      cancelled = true;
      cancelScheduledPreload();
    };
  }, []);

  // Handle cart icon click - open the shopping cart modal
  const handleCartClick = () => {
    // Always open the cart modal - the UnifiedCheckoutButton inside will handle the embedded checkout
    openCart();
  };

  const isHomePage = location.pathname === "/";
  const mainPaddingClass = isHomePage ? "pb-20" : "pb-28 sm:pb-36";

  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isLikelyChunkLoadError(event.reason)) {
        attemptChunkLoadRecovery(event.reason);
      }
    };

    const handleError = (event: ErrorEvent) => {
      if (isLikelyChunkLoadError(event.error ?? event.message)) {
        attemptChunkLoadRecovery(event.error ?? event.message);
      }
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("error", handleError);
    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
      window.removeEventListener("error", handleError);
    };
  }, []);

  useLayoutEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if ("scrollRestoration" in window.history) {
      const previousRestoration = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";

      return () => {
        window.history.scrollRestoration = previousRestoration;
      };
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (!shouldEnableAnalytics) {
      return;
    }

    checkoutAnalyticsService.trackPageView(location.pathname, {
      pageTitle:
        typeof document !== "undefined"
          ? document.title || undefined
          : undefined,
      pageSearch: location.search || undefined,
      pageHash: location.hash || undefined,
    });
  }, [
    location.pathname,
    location.search,
    location.hash,
    shouldEnableAnalytics,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (location.hash && location.hash !== "#") {
      const anchorId = location.hash.slice(1);
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      const attemptAnchorScroll = (attempt = 0) => {
        const target = document.getElementById(anchorId);
        if (target) {
          target.scrollIntoView({ block: "start", behavior: "smooth" });
          return;
        }

        if (attempt < 5) {
          timeoutId = setTimeout(() => attemptAnchorScroll(attempt + 1), 50);
        }
      };

      attemptAnchorScroll();

      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }

    const potentialTargets: Array<Element | Window> = [window];
    const scrollingElement = document.scrollingElement;
    const rootElement = document.getElementById("root");

    if (scrollingElement) {
      potentialTargets.push(scrollingElement);
    }

    if (rootElement) {
      potentialTargets.push(rootElement);
    }

    if (document.body) {
      potentialTargets.push(document.body);
    }

    if (document.documentElement) {
      potentialTargets.push(document.documentElement);
    }

    const uniqueTargets: Array<Element | Window> = [];
    const seenTargets = new Set<Element | Window>();

    potentialTargets.forEach((target) => {
      if (!target || seenTargets.has(target)) {
        return;
      }

      seenTargets.add(target);
      uniqueTargets.push(target);
    });

    const scrollToTop = () => {
      uniqueTargets.forEach((target) => {
        const anyTarget = target as unknown;
        const canScrollTo =
          typeof (anyTarget as { scrollTo?: unknown }).scrollTo === "function";

        if (canScrollTo) {
          (
            anyTarget as { scrollTo: (options: ScrollToOptions) => void }
          ).scrollTo({
            top: 0,
            left: 0,
            behavior: "auto",
          });
        }

        if (
          typeof (anyTarget as { scrollTop?: unknown }).scrollTop === "number"
        ) {
          (anyTarget as { scrollTop: number }).scrollTop = 0;
        }
      });
    };

    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      scrollToTop();
      secondFrame = window.requestAnimationFrame(scrollToTop);
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame) {
        window.cancelAnimationFrame(secondFrame);
      }
    };
  }, [location.pathname, location.search, location.hash]);

  const handleListenLiveClick = () => {
    playLiveStream();
  };

  return (
    <>
      <Helmet>
        <link
          rel="preconnect"
          href={IMAGE_CDN_ORIGIN}
          crossOrigin="anonymous"
        />
        <link rel="dns-prefetch" href={IMAGE_CDN_ORIGIN} />
      </Helmet>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-4 focus:left-4 focus:bg-gray-900 focus:text-white focus:px-4 focus:py-2 focus:rounded focus:outline-none focus:ring-2 focus:ring-red-500"
      >
        Skip to main content
      </a>
      <div
        className="min-h-screen"
        style={{
          background: createBackgroundGradient(mode),
        }}
      >
        {/* Header */}
        <header
          role="banner"
          className="border-b relative"
          style={{ borderColor: "var(--theme-border-primary)" }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
            <div className="flex flex-nowrap items-center justify-between gap-2 sm:grid sm:grid-cols-[auto_minmax(0,1fr)] sm:items-center sm:gap-4">
              <div className="flex items-end min-w-0">
                {/* Logo */}
                <Link
                  to="/"
                  className="flex items-end flex-shrink-0 max-w-[200px] sm:max-w-[420px] lg:max-w-[505px]"
                >
                  <span className="block sm:hidden w-full">
                    <OptimizedContentImage
                      src={eruptionLogoMobile}
                      alt="Eruption Radio"
                      className="h-10 w-auto"
                      imageClassName="!h-full !w-auto !object-contain"
                      imageStyle={{ objectFit: "contain", width: "auto", height: "100%" }}
                      sizes="160px"
                      priority
                      preload
                      preconnect
                    />
                  </span>
                  <span className="hidden sm:block w-full">
                    <OptimizedContentImage
                      src={eruptionLogo}
                      alt="Eruption Radio"
                      className="h-12 md:h-20 lg:h-24 w-auto"
                      imageClassName="!h-full !w-auto !object-contain"
                      imageStyle={{ objectFit: "contain", width: "auto", height: "100%" }}
                      sizes="(max-width: 1024px) 220px, 320px"
                      priority
                      preload
                      preconnect
                    />
                  </span>
                </Link>
              </div>
              <div className="flex flex-1 min-w-0 items-center justify-end gap-2 sm:gap-8">
                <div className="flex items-center gap-2 sm:hidden">
                  <button
                    type="button"
                    onClick={handleListenLiveClick}
                    className="px-3 py-1 rounded-full bg-red-800 text-white text-xs font-semibold shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-200 hover:bg-red-900 transition-colors flex-shrink-0"
                  >
                    Listen Live
                  </button>
                  <Link
                    to="/shows"
                    className="px-3 py-1 rounded-full bg-gray-800 text-white text-xs font-semibold border border-gray-700 shadow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 flex-shrink-0"
                  >
                    Shows
                  </Link>
                  {cartItemCount > 0 && (
                    <CartIcon
                      onClick={handleCartClick}
                      variant="minimal"
                      size="sm"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => setIsMobileNavOpen(true)}
                    className="p-2 text-gray-300 hover:text-white transition-colors"
                    aria-label="Open navigation menu"
                    aria-expanded={isMobileNavOpen}
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    </svg>
                  </button>
                </div>
                {/* Desktop navigation */}
                <div className="hidden sm:flex flex-wrap items-center justify-end gap-4 lg:gap-6">
                  <Navigation />

                  {/* Theme Toggle (temporarily disabled)
                  <ThemeToggle /> */}

                  {/* Cart Icon */}
                  <div className="relative">
                    <CartIcon onClick={handleCartClick} />
                  </div>
                </div>
                <div aria-live="polite" className="sr-only">
                  {cartAnnouncement}
                </div>
              </div>
            </div>
          </div>

          {/* TanStack Query DevTools - positioned in top-right corner */}
          {isDevEnvironment && ReactQueryDevtoolsLazy && (
            <div className="fixed bottom-4 right-4 z-50">
              <ChunkLoadErrorBoundary>
                <Suspense fallback={null}>
                  <ReactQueryDevtoolsLazy initialIsOpen={false} />
                </Suspense>
              </ChunkLoadErrorBoundary>
            </div>
          )}
        </header>

        {/* Main content - add bottom padding for audio player and mobile tab bar */}
        <main
          id="main-content"
          role="main"
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${mainPaddingClass}`}
        >
          <ChunkLoadErrorBoundary>
            <Suspense
              fallback={(
                <div className="min-h-[70vh] flex items-center justify-center">
                  <LoadingSpinner size="lg" text="Loading page..." />
                </div>
              )}
            >
              <Routes>
              <Route
                path="/"
                element={
                  <>
                    <Seo path="/" />
                    <HomePage />
                  </>
                }
              />
              <Route
                path="/schedule"
                element={
                  <>
                    <Seo
                      title="Schedule"
                      description="Check the Eruption Radio live schedule and see who's on air every day."
                      path="/schedule"
                    />
                    <SchedulePage />
                  </>
                }
              />
              <Route
                path="/shows"
                element={
                  <>
                    <Seo
                      title="Shows"
                      description="Browse Eruption Radio show replays and discover underground mixes from our resident DJs."
                      path="/shows"
                    />
                    <ShowsPage />
                  </>
                }
              />
              <Route
                path="/mixes"
                element={
                  <>
                    <Seo
                      title="Mixes"
                      description="Listen back to Eruption Radio live and studio mixes from DJs and events."
                      path="/mixes"
                    />
                    <MixesPage />
                  </>
                }
              />
              <Route path="/mixes/:mixSlug" element={<MixDetailPage />} />
              <Route path="/show/:showId" element={<ShowDetailsPage />} />
              <Route
                path="/presenters"
                element={
                  <>
                    <Seo
                      title="Presenters"
                      description="Meet the Eruption Radio presenters and learn more about the DJs behind the shows."
                      path="/presenters"
                    />
                    <PresentersPage />
                  </>
                }
              />
              <Route
                path="/presenters/:presenterSlug"
                element={<PresenterDetailPage />}
              />
              <Route
                path="/about"
                element={
                  <>
                    <Seo
                      title="About"
                      description="Discover the story of Eruption Radio and how we bring underground sounds to listeners worldwide."
                      path="/about"
                    />
                    <AboutPage />
                  </>
                }
              />
              <Route
                path="/blog"
                element={
                  <>
                    <Seo
                      title="Blog"
                      description="Read the latest news, interviews, and features from the Eruption Radio team."
                      path="/blog"
                    />
                    <BlogPage />
                  </>
                }
              />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route
                path="/events"
                element={
                  <>
                    <Seo
                      title="Events"
                      description="Explore upcoming Eruption Radio events, club nights, and special live broadcasts."
                      path="/events"
                    />
                    <EventsPage />
                  </>
                }
              />
              <Route path="/events/:slug" element={<EventPage />} />
              <Route
                path="/photos"
                element={
                  <>
                    <Seo
                      title="Photos"
                      description="Dive into photo galleries capturing the energy of Eruption Radio shows and events."
                      path="/photos"
                    />
                    <GalleriesPage />
                  </>
                }
              />
              <Route path="/photos/:slug" element={<GalleryPage />} />
              <Route
                path="/contact"
                element={
                  <>
                    <Seo
                      title="Contact"
                      description="Get in touch with the Eruption Radio team for shout-outs, bookings, sponsorships, or support."
                      path="/contact"
                    />
                    <ContactPage />
                  </>
                }
              />
              <Route
                path="/advertise"
                element={
                  <>
                    <Seo
                      title="Advertise"
                      description="Advertise with Eruption Radio UK and reach loyal listeners across DAB, app, website, and streaming platforms."
                      path="/advertise"
                    />
                    <AdvertisePage />
                  </>
                }
              />
              <Route
                path="/newsletter"
                element={
                  isNewsletterEnabled ? (
                    <>
                      <Seo
                        title="Newsletter"
                        description="Sign up for Eruption Radio news, show reminders, merch drops, and events."
                        path="/newsletter"
                        noindex
                      />
                      <NewsletterPage />
                    </>
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />
              <Route
                path="/privacy"
                element={
                  <>
                    <Seo
                      title="Privacy Policy"
                      description="Understand how Eruption Radio collects, uses, and protects your personal data."
                      path="/privacy"
                    />
                    <PrivacyPolicyPage />
                  </>
                }
              />
              <Route
                path="/terms"
                element={
                  <>
                    <Seo
                      title="Terms of Use"
                      description="Review the terms and conditions for using the Eruption Radio website and services."
                      path="/terms"
                    />
                    <TermsPage />
                  </>
                }
              />
              <Route
                path="/legal"
                element={
                  <>
                    <Seo
                      title="Legal Notice"
                      description="Company details, regulatory information, and contact points for Eruption Radio."
                      path="/legal"
                    />
                    <LegalNoticePage />
                  </>
                }
              />
              <Route
                path="/500"
                element={
                  <>
                    <Seo
                      title="Server Error"
                      description="Something went wrong while loading Eruption Radio. Please try again shortly."
                      path="/500"
                      noindex
                    />
                    <ServerErrorPage />
                  </>
                }
              />
              <Route
                path="/maintenance"
                element={
                  <>
                    <Seo
                      title="Scheduled Maintenance"
                      description="Eruption Radio is undergoing scheduled maintenance. We'll be back online soon."
                      path="/maintenance"
                      noindex
                    />
                    <MaintenancePage />
                  </>
                }
              />
              <Route
                path="/store"
                element={
                  <>
                    <Seo
                      title="Store"
                      description="Shop official Eruption Radio merchandise, vinyl, and accessories."
                      path="/store"
                    />
                    <UnifiedStorePage
                      defaultLoadingMode="infinite"
                      productsPerPage={24}
                      showModeToggle={false}
                    />
                  </>
                }
              />
              <Route
                path="/order-confirmation"
                element={
                  <>
                    <Seo
                      title="Order Confirmation"
                      description="Thank you for ordering from the Eruption Radio store."
                      path="/order-confirmation"
                      noindex
                    />
                    <OrderConfirmationPage />
                  </>
                }
              />
              <Route path="/listen" element={<Navigate to="/" replace />} />
              <Route
                path="/support"
                element={<Navigate to="/contact" replace />}
              />
              <Route
                path="/__sentry_test"
                element={
                  isSentryTestPageEnabled ? (
                    <>
                      <Seo title="Sentry Test" path="/__sentry_test" noindex />
                      <SentryTestPage />
                    </>
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />
              <Route
                path="*"
                element={
                  <>
                    <Seo
                      title="Page Not Found"
                      description="The page you requested could not be found on Eruption Radio."
                      path="/404"
                      noindex
                    />
                    <NotFoundPage />
                  </>
                }
              />
              </Routes>
            </Suspense>
          </ChunkLoadErrorBoundary>
        </main>

        <AnalyticsConsentBanner />
        <SiteFooter />

        {/* Global Audio Player */}
        <GlobalAudioPlayer />

        <ChunkLoadErrorBoundary>
          <Suspense fallback={null}>
            <MobileNavigationDrawer
              isOpen={isMobileNavOpen}
              onClose={() => setIsMobileNavOpen(false)}
              onNavigate={() => setIsMobileNavOpen(false)}
            />
          </Suspense>
        </ChunkLoadErrorBoundary>

        {/* Performance Dashboard (Development Only) - temporarily disabled
        {isDevEnvironment && PerformanceDashboard && (
          <Suspense fallback={null}>
            <PerformanceDashboard />
          </Suspense>
        )} */}

        {/* Shopping Cart Modal (Fallback) */}
        <ChunkLoadErrorBoundary>
          <Suspense
            fallback={
              isCartOpen ? (
                <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60">
                  <div className="bg-gray-900/90 border border-gray-700 rounded-2xl px-8 py-10 shadow-2xl">
                    <LoadingSpinner size="md" text="Loading cart..." />
                  </div>
                </div>
              ) : null
            }
          >
            <ShoppingCartModal isOpen={isCartOpen} onClose={closeCart} />
          </Suspense>
        </ChunkLoadErrorBoundary>
      </div>
    </>
  );
};

function App() {
  // Initialize performance services
  useEffect(() => {
    performanceServiceManager.initialize(queryClient);

    // Cleanup on unmount
    return () => {
      performanceServiceManager.destroy();
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AnalyticsConsentProvider>
        <Router>
          <GlobalErrorProvider
            onError={(entry) => globalErrorReportingService.reportError(entry)}
            maxErrors={200}
          >
            <ThemeProvider>
              <LiveShowProvider>
                <GlobalLiveShowChangeProvider>
                  <AudioPlayerProvider>
                    <CMSProvider>
                      <ShoppingCartProvider>
                        <ShoppingCartModalProvider>
                          <AddToCartFeedbackProvider>
                            <AppContent />
                          </AddToCartFeedbackProvider>
                        </ShoppingCartModalProvider>
                      </ShoppingCartProvider>
                    </CMSProvider>
                  </AudioPlayerProvider>
                </GlobalLiveShowChangeProvider>
              </LiveShowProvider>
            </ThemeProvider>
          </GlobalErrorProvider>
        </Router>
      </AnalyticsConsentProvider>
    </QueryClientProvider>
  );
}

export default App;
